<template>
	<div class="content">
		<base-header class="pb-6">
			<div class="row align-items-center py-4">
				<div class="col-lg-6 col-7">
					<h6 class="h2 text-white d-inline-block mb-0">{{ $t("pos.menu.bills") }}</h6>
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</div>
				<div class="col-lg-6 col-5 text-right">
					<base-button size="md" type="neutral" :disabled="loading" @click="exportXlsx">{{ $t("pos.common.export") }}</base-button>
				</div>
			</div>
		</base-header>

		<div class="container-fluid mt--6">
			<pos-install-alert :show="notInstalled"></pos-install-alert>
			<!-- table edge-to-edge in the card (as /backoffice/orders-overview): card body px-0, everything else in px-4 -->
			<card class="no-border-card" body-classes="px-0 pb-1">
				<div class="px-4">
					<pos-report-filter v-model="filter" @search="search"></pos-report-filter>
					<div class="row align-items-end">
						<div class="col-md-2 col-6 mb-2">
							<label class="form-control-label d-block">{{ $t("pos.filter.status") }}</label>
							<el-select v-model="status" class="w-100" @change="search">
								<el-option :label="$t('pos.filter.status_all')" value="all"></el-option>
								<el-option :label="$t('pos.filter.status_paid')" value="paid"></el-option>
								<el-option :label="$t('pos.filter.status_voided')" value="voided"></el-option>
							</el-select>
						</div>
						<div class="col-md-2 col-6 mb-2">
							<label class="form-control-label d-block">{{ $t("pos.filter.payment") }}</label>
							<el-select v-model="paymentMethod" class="w-100" @change="search">
								<el-option :label="$t('pos.filter.payment_all')" value=""></el-option>
								<el-option :label="$t('pos.bill.cash')" value="cash"></el-option>
								<el-option :label="$t('pos.bill.transfer')" value="transfer"></el-option>
							</el-select>
						</div>
						<div class="col-md-3 col-12 mb-2">
							<label class="form-control-label d-block">{{ $t("pos.filter.search") }}</label>
							<el-input v-model="q" clearable :placeholder="$t('pos.filter.search')" @keyup.enter.native="search" @clear="search"></el-input>
						</div>
						<!-- phase 3: buyer id (mid) filter, only with a chosen event -->
						<div class="col-md-2 col-6 mb-2">
							<label class="form-control-label d-block">{{ $t("pos.bill.mid_filter") }}</label>
							<el-tooltip :disabled="midEnabled" :content="$t('pos.bill.mid_needs_event')" placement="top">
								<el-input v-model="mid" clearable :disabled="!midEnabled" maxlength="7" placeholder="0042" @keyup.enter.native="search" @clear="search"></el-input>
							</el-tooltip>
						</div>
						<div class="col-md-1 col-6 mb-2 text-md-right">
							<base-button size="sm" type="primary" @click="search">{{ $t("pos.common.search") }}</base-button>
						</div>
					</div>

					<div class="pos-sum-strip row text-center my-2">
						<div class="col"><div class="text-muted small">{{ $t("pos.bill.sum_bills") }}</div><div class="h3 mb-0">{{ int(sums.bill_count) }}</div></div>
						<div class="col"><div class="text-muted small">{{ $t("pos.bill.sum_net") }}</div><div class="h3 mb-0">{{ money(sums.net) }}</div></div>
						<div class="col"><div class="text-muted small">{{ $t("pos.bill.sum_cash") }}</div><div class="h3 mb-0">{{ money(sums.cash_total) }}</div></div>
						<div class="col"><div class="text-muted small">{{ $t("pos.bill.sum_transfer") }}</div><div class="h3 mb-0">{{ money(sums.transfer_total) }}</div></div>
					</div>
				</div>

				<el-table :data="rows" v-loading="loading" header-row-class-name="thead-light" :row-class-name="rowClass" class="bon-table pos-table">
					<el-table-column :label="$t('pos.bill.bill_no')" min-width="170">
						<template slot-scope="{ row }">
							<a href="#" @click.prevent="view(row)">{{ row.bill_no }}</a>
						</template>
					</el-table-column>
					<el-table-column :label="$t('pos.bill.datetime')" prop="created_at" width="175" class-name="pos-nowrap"></el-table-column>
					<el-table-column :label="$t('pos.bill.device')" prop="device_code" min-width="90"></el-table-column>
					<el-table-column :label="$t('pos.bill.event')" min-width="160">
						<template slot-scope="{ row }">{{ row.event_name || $t("pos.filter.event_default") }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.bill.mid')" width="110" class-name="pos-nowrap">
						<template slot-scope="{ row }">{{ row.mid_text || "" }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.bill.seller')" prop="seller_name" min-width="130"></el-table-column>
					<el-table-column :label="$t('pos.bill.customer')" prop="customer_name" min-width="130"></el-table-column>
					<el-table-column :label="$t('pos.bill.phone')" prop="customer_phone" width="125" class-name="pos-nowrap"></el-table-column>
					<el-table-column :label="$t('pos.bill.qty')" prop="item_count" width="90" align="right" class-name="pos-nowrap" label-class-name="pos-nowrap"></el-table-column>
					<el-table-column :label="$t('pos.bill.subtotal')" width="110" align="right">
						<template slot-scope="{ row }">{{ money(row.subtotal) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.bill.discount')" width="100" align="right">
						<template slot-scope="{ row }">{{ money(row.discount_amount) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.bill.net')" width="110" align="right">
						<template slot-scope="{ row }"><strong>{{ money(row.total) }}</strong></template>
					</el-table-column>
					<el-table-column :label="$t('pos.bill.payment')" min-width="120">
						<template slot-scope="{ row }">{{ methodLabel(row.payment_method) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.bill.status')" width="130">
						<template slot-scope="{ row }">
							<el-tag v-if="row.status === 9" type="danger" size="small">{{ $t("pos.bill.voided_tag") }}</el-tag>
							<el-tag v-else type="success" size="small">{{ $t("pos.bill.paid_tag") }}</el-tag>
						</template>
					</el-table-column>
					<el-table-column :label="$t('pos.bill.void_reason')" prop="void_reason" min-width="150"></el-table-column>
					<el-table-column :label="$t('pos.bill.reprint_count')" prop="reprint_count" width="110" align="right"></el-table-column>
					<!-- row menu pinned at the right edge, same as /backoffice/orders-overview
					     (OrdersNew.vue): the popper is appended to <body>, so it opens from the
					     fixed-right copy too -->
					<el-table-column :label="$t('actions')" width="64" fixed="right" align="center" class-name="bon-tight">
						<template slot-scope="{ row }">
							<el-dropdown trigger="click" placement="bottom-end" @command="(c) => onCommand(c, row)">
								<base-button size="sm" type="secondary" icon data-testid="pos-row-menu" :aria-label="$t('pos.common.menu')" :title="$t('pos.common.menu')">
									<i class="fa fa-ellipsis-v"></i>
								</base-button>
								<el-dropdown-menu slot="dropdown" class="pos-row-menu">
									<el-dropdown-item command="view"><i class="fa fa-eye text-blue mr-2"></i>{{ $t("pos.bill.view") }}</el-dropdown-item>
									<el-dropdown-item command="reprint"><i class="fa fa-print text-info mr-2"></i>{{ $t("pos.bill.reprint") }}</el-dropdown-item>
									<el-dropdown-item v-if="row.status !== 9" command="void" divided><span class="text-danger"><i class="fa fa-ban mr-2"></i>{{ $t("pos.bill.void") }}</span></el-dropdown-item>
								</el-dropdown-menu>
							</el-dropdown>
						</template>
					</el-table-column>
				</el-table>
				<div class="d-flex justify-content-between align-items-center px-4 py-3">
					<small class="text-muted">{{ $t("pos.common.total_rows", { n: int(totalRows) }) }}</small>
					<el-pagination
						layout="prev, pager, next"
						:current-page.sync="page"
						:page-size="pageSize"
						:total="totalRows"
						@current-change="load"
					></el-pagination>
				</div>
			</card>
		</div>

		<el-dialog :title="$t('pos.bill.detail_title')" :visible.sync="viewVisible" width="420px">
			<pos-receipt-preview v-if="viewModel" :model="viewModel"></pos-receipt-preview>
			<span slot="footer">
				<base-button type="secondary" @click="viewVisible = false">{{ $t("pos.common.close") }}</base-button>
				<base-button type="primary" @click="reprint(viewRow)">{{ $t("pos.bill.reprint") }}</base-button>
			</span>
		</el-dialog>

		<el-dialog :title="$t('pos.bill.void_title')" :visible.sync="voidVisible" width="440px">
			<p class="mb-2"><strong>{{ voidRow && voidRow.bill_no }}</strong> — {{ voidRow && money(voidRow.total) }}</p>
			<label class="form-control-label">{{ $t("pos.bill.void_reason_label") }} *</label>
			<el-input v-model="voidReason" type="textarea" :rows="3" maxlength="255" show-word-limit></el-input>
			<small v-if="voidReasonShort" class="text-danger">{{ $t("pos.bill.void_reason_min") }}</small>
			<span slot="footer">
				<base-button type="secondary" @click="voidVisible = false">{{ $t("pos.common.cancel") }}</base-button>
				<base-button type="danger" :disabled="voidReasonShort || voiding" @click="doVoid">{{ $t("pos.bill.void") }}</base-button>
			</span>
		</el-dialog>

		<pos-receipt-print ref="printer"></pos-receipt-print>
	</div>
</template>

<script>
import { Table, TableColumn, Select, Option, Tag, Dialog, Dropdown, DropdownMenu, DropdownItem, Pagination, Tooltip } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { PosService } from "@/resource";
import { posMoney, posInt, POS_EXPORT_MAX_ROWS } from "@/utils/pos";
import posPageMixin, { posDefaultFilter } from "./posPageMixin";
import PosReportFilter from "./PosReportFilter.vue";
import PosInstallAlert from "./PosInstallAlert.vue";
import PosReceiptPreview from "./PosReceiptPreview.vue";
import PosReceiptPrint from "./PosReceiptPrint.vue";

export default {
	name: "PosBills",
	mixins: [posPageMixin],
	components: {
		RouteBreadCrumb,
		PosReportFilter,
		PosInstallAlert,
		PosReceiptPreview,
		PosReceiptPrint,
		[Table.name]: Table,
		[TableColumn.name]: TableColumn,
		[Select.name]: Select,
		[Option.name]: Option,
		[Tag.name]: Tag,
		[Dialog.name]: Dialog,
		[Dropdown.name]: Dropdown,
		[DropdownMenu.name]: DropdownMenu,
		[DropdownItem.name]: DropdownItem,
		[Pagination.name]: Pagination,
		[Tooltip.name]: Tooltip
	},
	data() {
		return {
			filter: posDefaultFilter(),
			status: "all",
			paymentMethod: "",
			q: "",
			mid: "",
			rows: [],
			sums: {},
			totalRows: 0,
			page: 1,
			pageSize: 20,
			viewVisible: false,
			viewModel: null,
			viewRow: null,
			voidVisible: false,
			voidRow: null,
			voidReason: "",
			voiding: false
		};
	},
	computed: {
		midEnabled() {
			return Number(this.filter && this.filter.eventId) > 0;
		},
		voidReasonShort() {
			return this.voidReason.trim().length < 3;
		}
	},
	mounted() {
		this.load();
	},
	methods: {
		money: posMoney,
		int: posInt,
		methodLabel(m) {
			return m === "transfer" ? this.$t("pos.bill.transfer") : this.$t("pos.bill.cash");
		},
		rowClass({ row }) {
			return row.status === 9 ? "pos-row-voided" : "";
		},
		params(extra) {
			const p = this.posParams(this.filter);
			if (!p) return null;
			p.status = this.status;
			if (this.paymentMethod) p.payment_method = this.paymentMethod;
			if (this.q.trim()) p.q = this.q.trim();
			if (this.midEnabled && this.mid.trim()) p.mid = this.mid.trim();
			return { ...p, ...(extra || {}) };
		},
		search() {
			this.page = 1;
			this.load();
		},
		async load() {
			const p = this.params({ page: this.page, page_size: this.pageSize });
			if (!p) return;
			this.loading = true;
			try {
				const res = await PosService.PosBillList(p);
				const d = res.data || {};
				this.rows = d.Data || [];
				const meta = d.Datum || {};
				this.totalRows = meta.total_rows || 0;
				this.sums = meta.sums || {};
				this.notInstalled = false;
			} catch (err) {
				this.rows = [];
				this.posError(err);
			} finally {
				this.loading = false;
			}
		},
		onCommand(cmd, row) {
			if (cmd === "view") this.view(row);
			else if (cmd === "reprint") this.reprint(row);
			else if (cmd === "void") this.openVoid(row);
		},
		async view(row) {
			try {
				const res = await PosService.PosBillGet(row.id);
				const d = (res.data && res.data.Datum) || {};
				this.viewModel = d.receipt_model || null;
				this.viewRow = row;
				this.viewVisible = true;
			} catch (err) {
				this.posError(err);
			}
		},
		async reprint(row) {
			if (!row) return;
			try {
				const res = await PosService.PosBillReprint(row.id);
				const d = (res.data && res.data.Datum) || {};
				if (d.receipt_model) await this.$refs.printer.print(d.receipt_model);
				this.load();
			} catch (err) {
				this.posError(err);
			}
		},
		openVoid(row) {
			this.voidRow = row;
			this.voidReason = "";
			this.voidVisible = true;
		},
		async doVoid() {
			if (this.voidReasonShort || !this.voidRow) return;
			this.voiding = true;
			try {
				await PosService.PosBillVoid(this.voidRow.id, { reason: this.voidReason.trim() });
				this.voidVisible = false;
				this.posToast("pos.bill.voided_ok");
				this.load();
			} catch (err) {
				this.posError(err);
			} finally {
				this.voiding = false;
			}
		},
		async exportXlsx() {
			const p = this.params({ export: 1 });
			if (!p) return;
			this.loading = true;
			try {
				const res = await PosService.PosBillList(p);
				const rows = (res.data && res.data.Data) || [];
				if (rows.length >= POS_EXPORT_MAX_ROWS) this.posToast("pos.common.export_capped");
				const t = (k) => this.$t(k);
				this.posExport("bills", p, [{
					name: t("pos.menu.bills"),
					rows,
					columns: [
						{ label: t("pos.bill.bill_no"), value: "bill_no" },
						{ label: t("pos.bill.datetime"), value: "created_at" },
						{ label: t("pos.bill.device"), value: "device_code" },
						{ label: t("pos.bill.event"), value: (r) => r.event_name || t("pos.filter.event_default") },
						{ label: t("pos.bill.mid"), value: (r) => r.mid_text || "" },
						{ label: t("pos.bill.seller"), value: "seller_name" },
						{ label: t("pos.bill.customer"), value: "customer_name" },
						{ label: t("pos.bill.phone"), value: "customer_phone" },
						{ label: t("pos.bill.qty"), value: "item_count" },
						{ label: t("pos.bill.subtotal"), value: (r) => Number(r.subtotal) },
						{ label: t("pos.bill.discount"), value: (r) => Number(r.discount_amount) },
						{ label: t("pos.bill.net"), value: (r) => Number(r.total) },
						{ label: t("pos.bill.payment"), value: (r) => this.methodLabel(r.payment_method) },
						{ label: t("pos.bill.status"), value: (r) => (r.status === 9 ? t("pos.bill.voided_tag") : t("pos.bill.paid_tag")) },
						{ label: t("pos.bill.void_reason"), value: "void_reason" },
						{ label: t("pos.bill.reprint_count"), value: "reprint_count" }
					]
				}]);
			} catch (err) {
				this.posError(err);
			} finally {
				this.loading = false;
			}
		}
	}
};
</script>

<style>
.pos-row-voided td {
	color: #525f7f !important;
	background: #f6f9fc !important;
}
.pos-nowrap .cell {
	white-space: nowrap;
}
.pos-sum-strip .col {
	border-right: 1px solid #e9ecef;
}
.pos-sum-strip .col:last-child {
	border-right: 0;
}
</style>
